import { useCallback, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Query-string backed state, replacing the unmaintained
 * `react-router-use-location-state` package (which only supported react-router
 * v5/v6). The signature is unchanged — `useQueryState(key, defaultValue)`
 * returns `[value, setValue]` — so call sites did not have to change.
 *
 * The encoding below deliberately matches what the old package wrote, so links
 * shared or bookmarked before this migration still open with their filters
 * applied:
 *
 *   boolean      `key=true` / `key=false`
 *   string       plain value
 *   number       `key=42`
 *   array        repeated keys — `key=a&key=b` (commas are literal, not separators)
 *   empty array  the sentinel below
 *
 * Two behaviours from the old package are also preserved: a value equal to its
 * default is removed from the URL entirely, and keys are kept sorted, so the
 * same state always produces the same URL.
 */

// `[`, a non-breaking space, `]` — the old package's empty-array marker.
// Written as an escape so the non-breaking space survives editing.
const EMPTY_ARRAY_STRING = '[ ]';

const readValue = (searchParams, key, defaultValue) => {
  if (!searchParams.has(key)) return defaultValue;

  if (Array.isArray(defaultValue)) {
    const values = searchParams.getAll(key);
    if (values.length === 1 && values[0] === EMPTY_ARRAY_STRING) return [];
    return values;
  }

  const raw = searchParams.get(key);

  if (typeof defaultValue === 'boolean') {
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return defaultValue;
  }

  if (typeof defaultValue === 'number') {
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? defaultValue : parsed;
  }

  return raw;
};

const isDefaultValue = (value, defaultValue) =>
  Array.isArray(defaultValue)
    ? JSON.stringify(value) === JSON.stringify(defaultValue)
    : value === defaultValue;

const withSortedKeys = (searchParams) => {
  const sorted = new URLSearchParams();
  const keys = [...new Set(searchParams.keys())].sort();
  keys.forEach((key) => {
    searchParams.getAll(key).forEach((value) => sorted.append(key, value));
  });
  return sorted;
};

const writeValue = (searchParams, key, value, defaultValue) => {
  const next = new URLSearchParams(searchParams);
  next.delete(key);

  if (!isDefaultValue(value, defaultValue)) {
    if (Array.isArray(value)) {
      if (value.length === 0) {
        next.append(key, EMPTY_ARRAY_STRING);
      } else {
        value.forEach((item) => next.append(key, item));
      }
    } else {
      next.append(key, String(value));
    }
  }

  return withSortedKeys(next);
};

const arraysEqual = (a, b) =>
  a.length === b.length && a.every((item, index) => item === b[index]);

export const useQueryState = (key, initialDefaultValue) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // The old package froze the default on first render; keep that so a default
  // created inline (e.g. `[]`) doesn't change identity on every render.
  const [defaultValue] = useState(initialDefaultValue);

  const rawValue = readValue(searchParams, key, defaultValue);

  // For arrays, readValue allocates a new array on every call (getAll(), or a
  // fresh `[]`), even when its contents are unchanged. Consumers that put the
  // value in a useEffect dependency array (e.g. Feed.js's itemType filter)
  // would otherwise re-run that effect every render, looping forever if the
  // effect itself calls setState. Keep the previous reference when equal.
  const stableArrayRef = useRef(rawValue);
  if (Array.isArray(rawValue)) {
    if (!arraysEqual(rawValue, stableArrayRef.current)) {
      stableArrayRef.current = rawValue;
    }
  } else {
    stableArrayRef.current = rawValue;
  }
  const value = stableArrayRef.current;

  // react-router re-creates setSearchParams whenever the URL's search string
  // changes, so depending on it directly would give consumers a new setter
  // after every write. Consumers that list the setter in a useEffect
  // dependency array (e.g. HoldingsOverviewPage's openItems reset) would then
  // re-run that effect after each URL update. Read the latest through a ref
  // instead, keeping the setter's identity fixed for the component's lifetime.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const setValue = useCallback(
    (nextValueOrFn) => {
      setSearchParamsRef.current(
        (current) => {
          const currentValue = readValue(current, key, defaultValue);
          const nextValue =
            typeof nextValueOrFn === 'function'
              ? nextValueOrFn(currentValue)
              : nextValueOrFn;

          return writeValue(current, key, nextValue, defaultValue);
        },
        // Filter changes shouldn't pile up in the back-button history.
        { replace: true }
      );
    },
    [key, defaultValue]
  );

  return [value, setValue];
};
